import { Button, CircularProgress, Box } from "@mui/material";
import { useState, useEffect, useRef, useCallback, useMemo } from "react";
import { useNavigate } from "react-router";
import { Autocomplete, GoogleSearchItemResult } from "../../parts/Autocomplete";
import { useGeoMap } from "../../context/GeoMapContext";
import { PublicCommune, usePublicCommunes } from "../../api/communeApi";

/**
 * SearchBar component on the Home page (Window 1).
 * <p>
 * Provides autocomplete search for serviced communes using our public backend API
 * (GET /v2/communes/public). Resolves the customerScope (AGS) and center coordinates,
 * validates lighting point availability, and navigates to /stoerung-melden with customerScope,
 * lat, and lng parameters.
 * </p>
 */
export const SearchBar = () => {
  const mounted = useRef(false);
  const navigate = useNavigate();
  const { customerScopeRef } = useGeoMap();
  const { data: publicCommunes, isLoading: isCommunesLoading } = usePublicCommunes();
  const geocoder = useRef<google.maps.Geocoder | null>(null);

  const [searchInput, setSearchInput] = useState("");
  const [selectedCommune, setSelectedCommune] = useState<PublicCommune>();
  const [isSearching, setIsSearching] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");

  // Map of unique key -> PublicCommune for fast lookup
  const communeMap = useMemo(() => {
    const map = new Map<string, PublicCommune>();
    if (publicCommunes) {
      publicCommunes.forEach((c) => {
        map.set(`${c.customerScope}_${c.zipCode}`, c);
      });
    }
    return map;
  }, [publicCommunes]);

  // Autocomplete options mapped from public communes (deduplicated by town & zipCode)
  const searchOptions: GoogleSearchItemResult[] = useMemo(() => {
    if (!publicCommunes) return [];
    const seen = new Set<string>();
    const options: GoogleSearchItemResult[] = [];

    // Prioritize standard municipal AGS over special Zweckverband (ZV_...)
    const sorted = [...publicCommunes].sort((a, b) => {
      const aIsZv = a.customerScope.includes("ZV_");
      const bIsZv = b.customerScope.includes("ZV_");
      if (aIsZv && !bIsZv) return 1;
      if (!aIsZv && bIsZv) return -1;
      return 0;
    });

    for (const c of sorted) {
      const description = `${c.town} (${c.zipCode})`;
      if (!seen.has(description)) {
        seen.add(description);
        options.push({
          description,
          place_id: `${c.customerScope}_${c.zipCode}`,
        });
      }
    }
    return options;
  }, [publicCommunes]);

  /**
   * Handles user input or option selection in the autocomplete.
   *
   * @param description - Text description entered or selected
   * @param placeId - Unique key (customerScope_zipCode) passed when selecting an option from dropdown
   */
  const onSelectedItemChange = (description: string, placeId?: string) => {
    setSearchInput(description);
    setErrorMessage("");

    if (placeId && communeMap.has(placeId)) {
      setSelectedCommune(communeMap.get(placeId));
      return;
    }

    if (publicCommunes) {
      const trimmed = description.trim().toLowerCase();
      const matched = publicCommunes.find(
        (c) =>
          `${c.town} (${c.zipCode})`.toLowerCase() === trimmed ||
          c.town.toLowerCase() === trimmed ||
          c.zipCode === trimmed
      );
      setSelectedCommune(matched);
    }
  };

  /**
   * Performs commune resolution, lighting points verification, and navigation to the fault reporting wizard.
   */
  const handleOnClick = useCallback(async () => {
    try {
      setIsSearching(true);
      setErrorMessage("");

      // Resolve commune from selection or search input
      let targetCommune = selectedCommune;
      if (!targetCommune && publicCommunes?.length) {
        const trimmed = searchInput.trim().toLowerCase();
        // Exact match by full label, town, or zipCode
        targetCommune = publicCommunes.find(
          (c) =>
            `${c.town} (${c.zipCode})`.toLowerCase() === trimmed ||
            c.town.toLowerCase() === trimmed ||
            c.zipCode === trimmed
        );

        // Substring / prefix match (prefer primary municipal AGS over ZV)
        if (!targetCommune) {
          const candidates = publicCommunes.filter(
            (c) =>
              c.town.toLowerCase().startsWith(trimmed) ||
              c.zipCode.startsWith(trimmed) ||
              c.town.toLowerCase().includes(trimmed)
          );
          targetCommune = candidates.find((c) => !c.customerScope.includes("ZV_")) || candidates[0];
        }
      }

      if (!targetCommune) {
        setErrorMessage(
          "Für diesen Standort sind wir leider nicht zuständig. Bitte wenden Sie sich an Ihre Stadt oder Gemeinde. Vielen Dank."
        );
        return;
      }

      // Determine center coordinates
      let lat = targetCommune.latitude;
      let lng = targetCommune.longitude;

      if (lat == null || lng == null) {
        if (!geocoder.current && typeof google !== "undefined" && google.maps) {
          geocoder.current = new google.maps.Geocoder();
        }
        if (geocoder.current) {
          const response = await geocoder.current.geocode({
            address: `${targetCommune.town}, ${targetCommune.zipCode}, Germany`,
          });
          const location = response.results[0]?.geometry.location;
          if (location) {
            lat = location.lat();
            lng = location.lng();
          }
        }
      }

      if (lat == null || lng == null) {
        setErrorMessage("Für diese Adresse konnten keine Geokoordinaten ermittelt werden.");
        return;
      }

      // Set customerScopeRef and navigate directly to fault reporting wizard with customerScope & coordinates.
      // Avoids downloading the entire commune lightpoints dataset upfront on the homepage (Ticket 5576273).
      customerScopeRef.current = targetCommune.customerScope;
      const queryParams = new URLSearchParams();
      queryParams.set("customerScope", targetCommune.customerScope);
      queryParams.set("lat", String(lat));
      queryParams.set("lng", String(lng));
      navigate(`/stoerung-melden?${queryParams}`);
    } catch (err) {
      console.error(`Error processing commune search for ${searchInput}`, err);
      setErrorMessage(
        "Es ist ein Fehler aufgetreten. Bitte versuchen Sie es später erneut."
      );
    } finally {
      if (mounted.current) {
        setIsSearching(false);
      }
    }
  }, [selectedCommune, publicCommunes, searchInput, customerScopeRef, navigate]);

  useEffect(() => {
    mounted.current = true;
    return () => {
      mounted.current = false;
    };
  }, []);

  // Pre-fill nearest serviced commune if user allows browser geolocation
  useEffect(() => {
    if (!publicCommunes || publicCommunes.length === 0) return;

    if (navigator.geolocation) {
      navigator.geolocation.getCurrentPosition(
        (position) => {
          const { latitude, longitude } = position.coords;
          let closest: PublicCommune | null = null;
          let minDistanceSq = Infinity;

          for (const c of publicCommunes) {
            if (c.latitude != null && c.longitude != null) {
              const dLat = c.latitude - latitude;
              const dLng = c.longitude - longitude;
              const distSq = dLat * dLat + dLng * dLng;
              if (distSq < minDistanceSq) {
                minDistanceSq = distSq;
                closest = c;
              }
            }
          }

          // Pre-fill only if closest commune is within ~20km (~0.05 deg^2) and input is empty
          if (closest && minDistanceSq < 0.05 && !searchInput) {
            setSelectedCommune(closest);
            setSearchInput(`${closest.town} (${closest.zipCode})`);
          }
        },
        (error) => {
          console.debug("Geolocation unavailable or denied", error);
        }
      );
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [publicCommunes]);

  return (
    <Box
      component="form"
      onSubmit={(e: React.FormEvent) => {
        e.preventDefault();
        handleOnClick();
      }}
      display="flex"
      flexDirection="column"
      gap="30px"
      alignItems="center"
      width="100%"
    >
      <Autocomplete
        id="home-location-autocomplete"
        placeholder="Ort, PLZ"
        error={!!errorMessage}
        errorMessage={errorMessage}
        onChange={onSelectedItemChange}
        className="w-full sm:w-2/3"
        options={searchOptions}
        value={searchInput}
        popupIcon={null}
        loading={isCommunesLoading}
        filterOptions={(options, state) => {
          const query = (searchInput || state.inputValue || "").trim().toLowerCase();
          if (!query) return options;
          const tokens = query.split(/\s+/).filter(Boolean);
          return options.filter((option) => {
            const desc = (typeof option === "string" ? option : option.description || "").toLowerCase();
            return tokens.every((token) => desc.includes(token));
          });
        }}
      />
      <Button
        color="primary"
        type="submit"
        style={{
          maxWidth: "300px",
        }}
        fullWidth
        variant="contained"
        disabled={!searchInput || isCommunesLoading}
      >
        {isSearching ? (
          <CircularProgress variant="indeterminate" style={{ color: "white", width: "26px", height: "26px" }} />
        ) : (
          "Leuchte finden und online melden"
        )}
      </Button>
    </Box>
  );
};

export default SearchBar;
